import { ForeignRates, Rates, RateSizes } from '@/types/parcel/userData/rates.interface';
import { Countries } from '@/utils/common';

interface Header {
  text: string;
  color?: string;
}

interface CountryHeaders {
  SPT?: Header[];
  SMD?: Header[];
  _99M?: Header[];
  CO2?: Header[];
}

interface Headers {
  default: Header[];
  MX?: CountryHeaders;
  CL?: CountryHeaders;
}

export const headers = (country: Countries): Headers => ({
  default: [
    { text: 'Tipo de envío', color: 'white' },
    { text: country === 'COL' ? 'XS *' : 'XS' },
    { text: country === 'COL' ? 'S *' : 'S' },
    { text: country === 'COL' ? 'M *' : 'M' },
    { text: 'L' },
    { text: 'XL' },
  ],
  MX: {
    SPT: [
      { text: 'Zonas', color: 'white' },
      { text: 'XS' },
      { text: 'S' },
      { text: 'M' },
      { text: 'L' },
      { text: 'XL' },
    ],
    SMD: [
      { text: ' ' },
      { text: 'XS' },
      { text: 'S' },
      { text: 'M' },
      { text: 'L' },
      { text: 'XL' },
    ],
    _99M: [
      { text: ' ' },
      { text: '5km' },
      { text: '15km' },
      { text: '30km' },
      { text: 'Hasta 50km' },
    ],
    CO2: [
      { text: ' ' },
      { text: 'XS' },
      { text: 'S' },
      { text: 'M' },
      { text: 'L' },
      { text: 'XL' },
    ],
  },
  CL: {
    SPT: [
      { text: 'Zonas', color: 'white' },
      { text: 'XS' },
      { text: 'S' },
      { text: 'M' },
      { text: 'L' },
      { text: 'XL' },
    ],
    SMD: [
      { text: ' ' },
      { text: 'XS' },
      { text: 'S' },
      { text: 'M' },
      { text: 'L' },
      { text: 'XL' },
    ],
  },
});

export const headerTitles: Record<string, string> = {
  SPT: 'Sprint',
  SMD: 'Same Day',
  _99M: '99minutos',
  CO2: 'CO2 Free',
};

export const countryServices = {
  MEX: ['SPT', 'SMD', '_99M', 'CO2'],
  CHL: ['SPT', 'SMD'],
};

export const regionsHeaders = [
  { text: 'Zona', color: 'white' },
  { text: 'Regiones', color: 'white' },
];

export const regionsRows = [
  [{ text: 'Anillo 1 / Local', bold: true }, { text: 'Metropolitana de Santiago' }],
  [{ text: 'Anillo 2 / Foráneo A', bold: true }, { text: "Valparaíso, O'Higgins" }],
  [{ text: 'Anillo 3 / Foráneo B', bold: true }, { text: 'Coquimbo, Maule, Ñuble, Biobío' }],
  [
    { text: 'Anillo 4 / Foráneo C', bold: true },
    { text: 'Atacama, Araucanía, Los Ríos, Los Lagos' },
  ],
  [
    { text: 'Anillo 5 / Foráneo D', bold: true },
    { text: 'Arica y Parinacota, Tarapacá, Antofagasta' },
  ],
  [{ text: 'Anillo 6 / Foráneo E', bold: true }, { text: 'Aysén, Magallanes' }],
];

const setRowText = (text: string) => {
  if (text === '_99M') return '99minutos';
  if (text === 'CO2') return 'CO2 Free';
  if (text === 'SPT') return 'Local';
  if (text === 'foreignA') return 'Foráneo A';
  if (text === 'foreignB') return 'Foráneo B';
  if (text === 'foreignC') return 'Foráneo C';
  if (text === 'foreignD') return 'Foráneo D';
  if (text === 'foreignE') return 'Foráneo E';
  if (text === 'SMD') return 'SameDay';
  if (text === 'NAL') return 'Nacional';
  if (text === 'RET') return 'Retorno';
  return text;
};

export interface Row {
  text: string;
  bold?: boolean;
}

const setRows = (rates: Rates, foreignRates: ForeignRates, t: any) => {
  const rows: Array<Array<Row>> = [];
  let ratesCopy: Rates | Rates[] = rates;
  if (!rates) return rows;

  if (!Array.isArray(rates) && rates.deliveryType !== 'SPT') {
    const row = [];

    row.push({ text: '', bold: true });

    orderSizes(row, ratesCopy.sizes, t);

    return row;
  }

  if (ratesCopy.deliveryType === 'SPT') {
    ratesCopy = formatRatesNXD(rates, foreignRates) as Rates[];
  }

  if (Array.isArray(ratesCopy)) {
    ratesCopy.map(({ deliveryType, sizes }) => {
      const row = [];
      row.push({ text: setRowText(deliveryType), bold: true });

      orderSizes(row, sizes, t);

      rows.push(row);
    });
  }

  return rows;
};

function formatRatesNXD(rates: Rates, foreignRates: ForeignRates) {
  const entries = Object.entries(foreignRates ?? []);

  const object = [rates];
  for (const element of entries) {
    const obj = {
      deliveryType: setRowText(element[0]),
      sizes: Object.entries(element[1].sizes).map(([key, value]) => {
        return { key, value: value as number };
      }),
    };
    object.push(obj);
  }
  object.sort((a, b) => {
    if (a.deliveryType === 'SPT') return -1;
    if (b.deliveryType === 'SPT') return 1;

    if (a.deliveryType < b.deliveryType) return -1;
    if (a.deliveryType > b.deliveryType) return 1;

    return 0;
  });

  return object;
}

const weights: Record<string, number> = {
  xs: 1,
  s: 2,
  m: 3,
  l: 4,
  xl: 5,
};

const orderSizes = (row: Row[], sizes: RateSizes[], t: any) => {
  const uniqueByKey = new Map<string, number>();
  sizes?.forEach(({ key, value }) => {
    const normalizedKey = key.toLowerCase();
    const existing = uniqueByKey.get(normalizedKey);
    if (existing === undefined || existing === 0) {
      uniqueByKey.set(normalizedKey, value);
    }
  });

  const sortedSizes = Array.from(uniqueByKey.entries())
    .filter(([key]) => weights[key] !== undefined)
    .sort(([keyA], [keyB]) => weights[keyA] - weights[keyB]);

  sortedSizes.forEach(([, value]) => {
    row.push({
      text: `${t('currencySymbol')} ${value}`,
      bold: false,
    });
  });
};

export const setHeaders = (country: Countries): CountryHeaders | Header[] => {
  if (country === 'MEX') {
    return headers(country).MX ?? [];
  }

  if (country === 'CHL') {
    return headers(country).CL ?? [];
  }

  return headers(country).default;
};

export const handleClick = () => {
  const url = 'mailto: atencion@99minutos.com';
  const link = document.createElement('a');
  link.href = url;
  link.click();
};

export interface Table {
  title: string;
  headers: Array<{ text: string; color?: string }>;
  rows: Row[][] | Row[];
}
export const generateTableObject = (
  header: CountryHeaders | Header[],
  rates: Rates[],
  foreignRates: ForeignRates,
  country: Countries,
  t: any,
) => {
  const deliveriesTypes = ['SPT', 'SMD', '_99M', 'CO2'];
  const table: Table[] = [];
  const ratesFilter = rates?.filter((rate) => deliveriesTypes.includes(rate.deliveryType));

  const sortedRates = ratesFilter?.sort((a, b) => {
    const aIndex = deliveriesTypes.indexOf(a.deliveryType);
    const bIndex = deliveriesTypes.indexOf(b.deliveryType);

    return aIndex - bIndex;
  });

  // Mostrar unicamente los rates que correspondan al country del cliente basado en countryServices
  const sortedRatesFiltered = sortedRates?.filter((rate) =>
    (countryServices[country as keyof typeof countryServices] || []).includes(rate.deliveryType),
  );

  sortedRatesFiltered?.forEach((rate) => {
    const obj = {
      title: headerTitles[rate.deliveryType],
      headers: (header as CountryHeaders)[rate.deliveryType as keyof CountryHeaders] || [],
      rows: setRows(rate, foreignRates, t),
    };
    table.push(obj);
  });

  return table;
};
